import { useMemo } from "react";
import { Combobox } from "@/components/ui";
import {
  ReviewFiltersPopoverLayout,
  ReviewSentimentFilterButtons,
  ReviewSourceFilterButtons,
  ReviewTriageFilterButtons,
  ReviewTimeFrameFilter,
  ReviewRatingTypeFilterButtons,
} from "@/components/reviews";
import { useSegmentation } from "@/utils";
import { ReviewRatingValueFilter } from "./ReviewRatingValueFilter";
import { useCoachReviewsFilters } from "./CoachReviewsFiltersContext";

type ReviewsFiltersPopoverProps = {
  onClose: () => void;
  side?: "right" | "bottom";
  resultCount?: number | null;
};

export function ReviewsFiltersPopover({ onClose, side = "right", resultCount }: ReviewsFiltersPopoverProps) {
  const {
    players,
    playerId,
    clearAllPopoverFilters,
    filters: state,
    segmentationIds,
    setSegmentationIds,
    setSourceSystem,
    setSentiment,
    setTriage,
    setDatePreset,
    setRatingType,
    setRatingValue,
    setNpsCategory,
    setRecommendation,
  } = useCoachReviewsFilters();
  const { segmentationOptions } = useSegmentation(["REGION", "BRANCH"]);

  const selectedSegmentations = useMemo(
    () => segmentationOptions.filter(option => segmentationIds.includes(Number(option.value))),
    [segmentationOptions, segmentationIds]
  );

  const hasPopoverFilters = Boolean(
    playerId ||
      segmentationIds.length ||
      state.triage ||
      state.sourceSystem ||
      state.sentiment ||
      state.ratingType ||
      state.ratingValue != null ||
      state.npsCategory ||
      state.recommendation ||
      state.datePreset ||
      state.startDate ||
      state.endDate
  );

  return (
    <ReviewFiltersPopoverLayout
      side={side}
      initialFocus={false}
      onClear={hasPopoverFilters ? clearAllPopoverFilters : undefined}
      onDone={onClose}
      resultCount={resultCount}
    >
      <div className="flex flex-col gap-2">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Player</label>
        <Combobox {...players} showClear placeholder="Filter by Player..." className="w-full bg-white" />
      </div>

      {segmentationOptions.length > 0 ? (
        <div className="flex flex-col gap-2">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Segmentation</label>
          <Combobox
            items={segmentationOptions}
            value={selectedSegmentations}
            showClear
            searchable
            multiple
            maxChips="auto"
            placeholder="Filter by Segmentation..."
            className="w-full bg-white"
            itemToStringLabel={item => item.text}
            itemToBadge={item => item.label.toUpperCase()}
            onValueChange={value => setSegmentationIds((value ?? []).map(option => Number(option.value)))}
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Review Status</label>
        <ReviewTriageFilterButtons value={state.triage} onChange={setTriage} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Platform</label>
          <ReviewSourceFilterButtons value={state.sourceSystem} onChange={setSourceSystem} />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Sentiment</label>
          <ReviewSentimentFilterButtons value={state.sentiment} onChange={setSentiment} />
        </div>
      </div>

      <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Rating Type</label>
        <ReviewRatingTypeFilterButtons value={state.ratingType} onChange={setRatingType} />
        {state.ratingType ? (
          <div className="flex flex-col gap-2 border-t border-slate-100 pt-3">
            <ReviewRatingValueFilter
              ratingType={state.ratingType}
              value={state.ratingValue}
              onChange={setRatingValue}
              npsCategory={state.npsCategory}
              onNpsCategoryChange={setNpsCategory}
              recommendation={state.recommendation}
              onRecommendationChange={setRecommendation}
            />
          </div>
        ) : null}
      </div>

      <div className="pt-2 border-t border-slate-100">
        <ReviewTimeFrameFilter
          preset={state.datePreset}
          startDate={state.startDate}
          endDate={state.endDate}
          onChange={setDatePreset}
        />
      </div>
    </ReviewFiltersPopoverLayout>
  );
}
